import canonicalizePkg from 'canonicalize';

/**
 * RFC 8785 JSON Canonicalization Scheme (JCS).
 *
 * Delegates to the `canonicalize` package (Apache-2.0, by Samuel Erdtman, who
 * authored RFC 8785). Wrapped here to: (a) reject the inputs JCS leaves
 * undefined (non-finite numbers, BigInt) instead of silently coercing, and
 * (b) preserve our existing call sites and tests.
 */
export function canonicalize(value: unknown): string {
  assertJcsCompatible(value);
  const out = canonicalizePkg(value);
  if (out === undefined) {
    throw new TypeError('JCS: canonicalize() returned undefined');
  }
  return out;
}

export function canonicalizeBytes(value: unknown): Uint8Array {
  return new TextEncoder().encode(canonicalize(value));
}

function assertJcsCompatible(value: unknown): void {
  if (typeof value === 'number') {
    if (!Number.isFinite(value)) {
      throw new TypeError(`JCS: non-finite number ${String(value)}`);
    }
    return;
  }
  if (typeof value === 'bigint') {
    throw new TypeError('JCS: bigint not supported by RFC 8785');
  }
  if (Array.isArray(value)) {
    for (const item of value) assertJcsCompatible(item);
    return;
  }
  if (value !== null && typeof value === 'object') {
    for (const v of Object.values(value as Record<string, unknown>)) {
      if (v !== undefined) assertJcsCompatible(v);
    }
  }
}
